// =============================================================================
// PDF DU PLANNING HEBDOMADAIRE ET MENSUEL
// =============================================================================

import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from '@react-pdf/renderer';
import { format, startOfWeek, endOfWeek, eachDayOfInterval, startOfMonth, endOfMonth, isWeekend, getWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { COLORS, sharedStyles } from './pdfStyles';
import type { CalendarAffectation } from '@/types/calendar';

// =============================================================================
// STYLES SPÉCIFIQUES AU PLANNING
// =============================================================================

const styles = StyleSheet.create({
  pageOverrides: {
    paddingBottom: 40,
  },
  content: {
    paddingHorizontal: 30,
    paddingTop: 20,
  },
  // Grille semaine
  weekGrid: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: COLORS.grisClair,
    borderRadius: 4,
    overflow: 'hidden',
  },
  dayColumn: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: COLORS.grisClair,
    minHeight: 120,
  },
  dayColumnLast: {
    borderRightWidth: 0,
  },
  dayHeader: {
    backgroundColor: COLORS.bleuRoi,
    paddingVertical: 8,
    paddingHorizontal: 4,
    alignItems: 'center',
  },
  dayHeaderWeekend: {
    backgroundColor: '#92400E', // amber-800
  },
  dayHeaderToday: {
    backgroundColor: '#059669', // emerald-600
  },
  dayName: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  dayNumber: {
    fontSize: 14,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    marginTop: 2,
  },
  dayMonth: {
    fontSize: 7,
    color: COLORS.dore,
  },
  dayContent: {
    padding: 4,
    flex: 1,
  },
  affectationBadge: {
    backgroundColor: COLORS.bleuClair,
    borderRadius: 3,
    padding: 4,
    marginBottom: 3,
  },
  affectationChantier: {
    fontSize: 7,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
  },
  affectationMembres: {
    fontSize: 6,
    color: COLORS.grisTexte,
    marginTop: 1,
  },
  noAffectation: {
    fontSize: 6,
    color: '#9CA3AF',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 8,
  },
  // Grille mois
  monthGrid: {
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 4,
    overflow: 'hidden',
  },
  monthHeader: {
    flexDirection: 'row',
    backgroundColor: COLORS.bleuRoi,
  },
  monthHeaderCell: {
    flex: 1,
    paddingVertical: 6,
    alignItems: 'center',
    borderRightWidth: 1,
    borderRightColor: '#2D4A7C',
  },
  monthHeaderCellLast: {
    borderRightWidth: 0,
  },
  monthHeaderText: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.blanc,
    textTransform: 'uppercase',
  },
  monthWeekRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#CBD5E1',
    minHeight: 60,
  },
  monthWeekRowLast: {
    borderBottomWidth: 0,
  },
  monthDayCell: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: '#CBD5E1',
    padding: 3,
  },
  monthDayCellLast: {
    borderRightWidth: 0,
  },
  monthDayCellWeekend: {
    backgroundColor: '#FEF3C7',
  },
  monthDayCellOtherMonth: {
    backgroundColor: '#F9FAFB',
  },
  monthDayNumber: {
    fontSize: 8,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.grisFonce,
    marginBottom: 2,
  },
  monthDayNumberOther: {
    color: '#D1D5DB',
  },
  monthAffectationBadge: {
    backgroundColor: COLORS.bleuClair,
    borderRadius: 2,
    padding: 2,
    marginBottom: 2,
  },
  monthAffectationText: {
    fontSize: 5,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
  },
  monthMoreBadge: {
    backgroundColor: '#E5E7EB',
    borderRadius: 2,
    padding: 2,
  },
  monthMoreText: {
    fontSize: 5,
    color: COLORS.grisTexte,
    textAlign: 'center',
  },
  // Légende
  legendSection: {
    marginTop: 15,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: COLORS.grisClair,
  },
  legendTitle: {
    fontSize: 9,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
    marginBottom: 8,
  },
  legendGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 15,
    marginBottom: 4,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 4,
  },
  legendText: {
    fontSize: 7,
    color: COLORS.grisTexte,
  },
  // Stats
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 15,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: COLORS.grisClair,
  },
  statBox: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 8,
    marginHorizontal: 4,
    backgroundColor: COLORS.grisClair,
    borderRadius: 4,
  },
  statValue: {
    fontSize: 16,
    fontFamily: 'Helvetica-Bold',
    color: COLORS.bleuRoi,
  },
  statLabel: {
    fontSize: 7,
    color: COLORS.grisTexte,
    marginTop: 2,
    textTransform: 'uppercase',
  },
});

// =============================================================================
// INTERFACES
// =============================================================================

// Interface pour les filtres actifs
interface ActiveFilters {
  chantierNames?: string[];   // Noms des chantiers filtrés
  equipeNames?: string[];     // Noms des équipes filtrées
  searchQuery?: string;       // Recherche texte
}

interface PlanningPDFProps {
  viewMode: 'semaine' | 'mois';
  currentDate: Date;
  affectations: CalendarAffectation[];
  entrepriseNom?: string;
  activeFilters?: ActiveFilters;
}

// =============================================================================
// COMPOSANTS HELPER
// =============================================================================

const Header = ({ viewMode, currentDate, entrepriseNom, activeFilters }: { viewMode: string; currentDate: Date; entrepriseNom?: string; activeFilters?: ActiveFilters }) => {
  const titre = viewMode === 'semaine'
    ? `Semaine ${getWeek(currentDate, { weekStartsOn: 1 })}`
    : format(currentDate, 'MMMM yyyy', { locale: fr });

  // Construire le sous-titre avec les filtres actifs
  const hasFilters = activeFilters && (
    (activeFilters.chantierNames && activeFilters.chantierNames.length > 0) ||
    (activeFilters.equipeNames && activeFilters.equipeNames.length > 0) ||
    activeFilters.searchQuery
  );

  // Titre du filtre pour affichage proéminent
  let filterTitle = '';
  if (activeFilters?.chantierNames && activeFilters.chantierNames.length === 1) {
    filterTitle = activeFilters.chantierNames[0];
  } else if (activeFilters?.equipeNames && activeFilters.equipeNames.length === 1) {
    filterTitle = `Equipe ${activeFilters.equipeNames[0]}`;
  } else if (activeFilters?.searchQuery) {
    filterTitle = `Recherche: ${activeFilters.searchQuery}`;
  }

  return (
    <>
      <View style={sharedStyles.header}>
        <View style={sharedStyles.headerLeft}>
          <Text style={sharedStyles.logoText}>B<Text style={{ color: '#F59E0B' }}>8</Text>ild</Text>
        </View>
        <View style={sharedStyles.headerRight}>
          <Text style={sharedStyles.headerTitle}>Planning des equipes</Text>
          <Text style={sharedStyles.headerSubtitle}>
            {entrepriseNom || 'Entreprise'} - {format(new Date(), 'dd/MM/yyyy')}
          </Text>
        </View>
      </View>
      <View style={sharedStyles.bandeDore} />
      <View style={styles.content}>
        {/* Titre principal : filtre ou période */}
        {filterTitle ? (
          <>
            <Text style={{ fontSize: 20, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi, marginBottom: 4 }}>
              {filterTitle}
            </Text>
            <Text style={{ fontSize: 12, color: COLORS.grisTexte, marginBottom: 15, textTransform: 'capitalize' }}>
              {titre}
            </Text>
          </>
        ) : (
          <Text style={{ fontSize: 18, fontFamily: 'Helvetica-Bold', color: COLORS.bleuRoi, marginBottom: 15, textTransform: 'capitalize' }}>
            {titre}
          </Text>
        )}

        {/* Badges des filtres multiples */}
        {hasFilters && (activeFilters?.chantierNames?.length > 1 || activeFilters?.equipeNames?.length > 1) && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
            {activeFilters?.chantierNames?.map((name, idx) => (
              <View key={`c-${idx}`} style={{ backgroundColor: COLORS.bleuClair, borderRadius: 4, paddingVertical: 3, paddingHorizontal: 8 }}>
                <Text style={{ fontSize: 8, color: COLORS.bleuRoi, fontFamily: 'Helvetica-Bold' }}>
                  {name}
                </Text>
              </View>
            ))}
            {activeFilters?.equipeNames?.map((name, idx) => (
              <View key={`e-${idx}`} style={{ backgroundColor: '#D1FAE5', borderRadius: 4, paddingVertical: 3, paddingHorizontal: 8 }}>
                <Text style={{ fontSize: 8, color: '#065F46', fontFamily: 'Helvetica-Bold' }}>
                  {name}
                </Text>
              </View>
            ))}
          </View>
        )}
      </View>
    </>
  );
};

const Footer = () => (
  <View
    style={{
      position: 'absolute',
      bottom: 20,
      left: 30,
      right: 30,
      flexDirection: 'row',
      justifyContent: 'space-between',
      borderTopWidth: 1,
      borderTopColor: COLORS.grisClair,
      paddingTop: 8,
    }}
    fixed
  >
    <Text style={{ fontSize: 7, color: COLORS.grisTexte }}>
      Genere par B8ild le {format(new Date(), 'dd/MM/yyyy a HH:mm', { locale: fr })}
    </Text>
    <Text
      style={{ fontSize: 7, color: COLORS.grisTexte }}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} / ${totalPages}`}
    />
  </View>
);

// =============================================================================
// HELPER : Vérifier si une affectation doit s'afficher pour un jour donné
// =============================================================================

function shouldShowOnDay(aff: CalendarAffectation, day: Date): boolean {
  const dateStr = format(day, 'yyyy-MM-dd');

  // Normaliser les dates (comparaison par chaîne YYYY-MM-DD)
  const dayStr = dateStr;
  const debutStr = aff.date_debut.split('T')[0];
  const finStr = aff.date_fin.split('T')[0];

  // Vérifier si le jour est dans la plage de dates
  if (dayStr < debutStr || dayStr > finStr) {
    return false;
  }

  // Si jours_details est disponible, utiliser cette info précise
  if (aff.jours_details && Array.isArray(aff.jours_details) && aff.jours_details.length > 0) {
    return aff.jours_details.some((jd: { date: string }) => jd.date === dateStr);
  }

  // Fallback: logique classique pour les weekends
  if (isWeekend(day)) {
    const debut = new Date(debutStr);
    const fin = new Date(finStr);
    let joursOuvres = 0;
    const current = new Date(debut);
    while (current <= fin) {
      if (!isWeekend(current)) joursOuvres++;
      current.setDate(current.getDate() + 1);
    }
    const joursTravailles = aff.jours_travailles || 0;
    return joursTravailles > joursOuvres;
  }

  return true;
}

// =============================================================================
// VUE SEMAINE
// =============================================================================

const WeekView = ({ currentDate, affectations }: { currentDate: Date; affectations: CalendarAffectation[] }) => {
  const start = startOfWeek(currentDate, { weekStartsOn: 1 });
  const end = endOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = eachDayOfInterval({ start, end });
  const today = new Date();
  const todayStr = format(today, 'yyyy-MM-dd');

  // Grouper les affectations par jour et par chantier
  const getAffectationsForDay = (day: Date) => {
    // Utiliser shouldShowOnDay pour une comparaison correcte des dates
    const dayAffectations = affectations.filter((aff) => shouldShowOnDay(aff, day));

    // Grouper par chantier
    const byChantier: Record<string, CalendarAffectation[]> = {};
    dayAffectations.forEach((aff) => {
      const chantierId = aff.chantier_id;
      if (!byChantier[chantierId]) {
        byChantier[chantierId] = [];
      }
      byChantier[chantierId].push(aff);
    });

    return Object.values(byChantier).map((chantierAffectations) => {
      // Extraire les équipes uniques
      const equipesMap = new Map<string, { nom: string }>();
      chantierAffectations.forEach(aff => {
        if (aff.equipe?.id && !equipesMap.has(aff.equipe.id)) {
          equipesMap.set(aff.equipe.id, { nom: aff.equipe.nom });
        }
      });

      return {
        chantier: chantierAffectations[0]?.chantier,
        memberCount: chantierAffectations.length,
        equipes: Array.from(equipesMap.values()),
      };
    });
  };

  return (
    <View style={styles.content}>
      <View style={styles.weekGrid}>
        {weekDays.map((day, index) => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const isWeekendDay = isWeekend(day);
          const isToday = dateKey === todayStr;
          const dayAffectations = getAffectationsForDay(day);

          return (
            <View
              key={dateKey}
              style={[
                styles.dayColumn,
                index === weekDays.length - 1 && styles.dayColumnLast,
              ]}
            >
              <View
                style={[
                  styles.dayHeader,
                  isWeekendDay && styles.dayHeaderWeekend,
                  isToday && styles.dayHeaderToday,
                ]}
              >
                <Text style={styles.dayName}>{format(day, 'EEE', { locale: fr })}</Text>
                <Text style={styles.dayNumber}>{format(day, 'd')}</Text>
                <Text style={styles.dayMonth}>{format(day, 'MMM', { locale: fr })}</Text>
              </View>
              <View style={styles.dayContent}>
                {dayAffectations.length === 0 ? (
                  <Text style={styles.noAffectation}>-</Text>
                ) : (
                  dayAffectations.slice(0, 4).map((grouped, idx) => {
                    const couleur = grouped.chantier?.couleur || '#3B82F6';
                    return (
                      <View
                        key={idx}
                        style={{
                          marginBottom: 4,
                          borderRadius: 4,
                          backgroundColor: couleur,
                          padding: 5,
                        }}
                      >
                        {/* Ligne 1 : Nom chantier + badge membres */}
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#FFFFFF' }}>
                            {grouped.chantier?.nom_chantier?.substring(0, 10) || 'Chantier'}
                          </Text>
                          <View style={{
                            backgroundColor: 'rgba(255,255,255,0.3)',
                            borderRadius: 8,
                            paddingHorizontal: 5,
                            paddingVertical: 2,
                          }}>
                            <Text style={{ fontSize: 7, color: '#FFFFFF', fontFamily: 'Helvetica-Bold' }}>
                              {grouped.memberCount}
                            </Text>
                          </View>
                        </View>
                        {/* Ligne 2 : Équipes */}
                        {grouped.equipes.length > 0 && (
                          <Text style={{ fontSize: 7, color: 'rgba(255,255,255,0.9)', marginTop: 2 }}>
                            {grouped.equipes.map(e => e.nom).join(', ')}
                          </Text>
                        )}
                      </View>
                    );
                  })
                )}
                {dayAffectations.length > 5 && (
                  <Text style={{ fontSize: 6, color: COLORS.grisTexte, textAlign: 'center' }}>
                    +{dayAffectations.length - 5} autres
                  </Text>
                )}
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

// =============================================================================
// VUE MOIS
// =============================================================================

const MonthView = ({ currentDate, affectations }: { currentDate: Date; affectations: CalendarAffectation[] }) => {
  const start = startOfMonth(currentDate);
  const end = endOfMonth(currentDate);
  const monthStart = startOfWeek(start, { weekStartsOn: 1 });
  const monthEnd = endOfWeek(end, { weekStartsOn: 1 });
  const allDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Diviser en semaines
  const weeks: Date[][] = [];
  for (let i = 0; i < allDays.length; i += 7) {
    weeks.push(allDays.slice(i, i + 7));
  }

  const joursSemaine = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  // Utiliser shouldShowOnDay pour une comparaison correcte des dates
  const getAffectationsForDay = (day: Date) => {
    return affectations.filter((aff) => shouldShowOnDay(aff, day));
  };

  return (
    <View style={styles.content}>
      <View style={styles.monthGrid}>
        {/* Header */}
        <View style={styles.monthHeader}>
          {joursSemaine.map((jour, idx) => (
            <View
              key={jour}
              style={[
                styles.monthHeaderCell,
                idx === joursSemaine.length - 1 && styles.monthHeaderCellLast,
              ]}
            >
              <Text style={styles.monthHeaderText}>{jour}</Text>
            </View>
          ))}
        </View>

        {/* Semaines */}
        {weeks.map((week, weekIdx) => (
          <View
            key={weekIdx}
            style={[
              styles.monthWeekRow,
              weekIdx === weeks.length - 1 && styles.monthWeekRowLast,
            ]}
          >
            {week.map((day, dayIdx) => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const isCurrentMonth = day.getMonth() === currentDate.getMonth();
              const isWeekendDay = isWeekend(day);
              const dayAffectations = getAffectationsForDay(day);

              // Grouper par chantier
              const byChantier: Record<string, CalendarAffectation[]> = {};
              dayAffectations.forEach((aff) => {
                if (!byChantier[aff.chantier_id]) {
                  byChantier[aff.chantier_id] = [];
                }
                byChantier[aff.chantier_id].push(aff);
              });
              const groupedAffectations = Object.values(byChantier);

              return (
                <View
                  key={dateKey}
                  style={[
                    styles.monthDayCell,
                    dayIdx === week.length - 1 && styles.monthDayCellLast,
                    isWeekendDay && styles.monthDayCellWeekend,
                    !isCurrentMonth && styles.monthDayCellOtherMonth,
                  ]}
                >
                  <Text
                    style={[
                      styles.monthDayNumber,
                      !isCurrentMonth && styles.monthDayNumberOther,
                    ]}
                  >
                    {format(day, 'd')}
                  </Text>
                  {groupedAffectations.slice(0, 2).map((chantierAffs, idx) => {
                    const couleur = chantierAffs[0]?.chantier?.couleur || '#3B82F6';
                    return (
                      <View
                        key={idx}
                        style={{
                          marginBottom: 2,
                          borderRadius: 2,
                          backgroundColor: couleur,
                          padding: 2,
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Text style={{ fontSize: 6, fontFamily: 'Helvetica-Bold', color: '#FFFFFF' }}>
                          {chantierAffs[0]?.chantier?.nom_chantier?.substring(0, 6) || '?'}
                        </Text>
                        <Text style={{ fontSize: 6, color: 'rgba(255,255,255,0.8)', fontFamily: 'Helvetica-Bold' }}>
                          {chantierAffs.length}
                        </Text>
                      </View>
                    );
                  })}
                  {groupedAffectations.length > 2 && (
                    <View style={styles.monthMoreBadge}>
                      <Text style={styles.monthMoreText}>+{groupedAffectations.length - 2}</Text>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
};

// =============================================================================
// STATISTIQUES
// =============================================================================

const Stats = ({ affectations, currentDate, viewMode }: { affectations: CalendarAffectation[]; currentDate: Date; viewMode: string }) => {
  // Calculer les stats pour la période
  const start = viewMode === 'semaine'
    ? startOfWeek(currentDate, { weekStartsOn: 1 })
    : startOfMonth(currentDate);
  const end = viewMode === 'semaine'
    ? endOfWeek(currentDate, { weekStartsOn: 1 })
    : endOfMonth(currentDate);

  const periodAffectations = affectations.filter((aff) => {
    const debut = new Date(aff.date_debut);
    const fin = new Date(aff.date_fin);
    return fin >= start && debut <= end;
  });

  const uniqueChantiers = new Set(periodAffectations.map((a) => a.chantier_id)).size;
  const uniqueMembres = new Set(periodAffectations.map((a) => a.membre_equipe_id)).size;
  const totalJours = periodAffectations.reduce((sum, a) => sum + (a.jours_travailles || 0), 0);

  return (
    <View style={[styles.content, { paddingTop: 0 }]}>
      <View style={styles.statsRow}>
        <View style={styles.statBox}>
          <Text style={styles.statValue}>{uniqueChantiers}</Text>
          <Text style={styles.statLabel}>Chantiers</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statValue}>{uniqueMembres}</Text>
          <Text style={styles.statLabel}>Membres</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statValue}>{totalJours}</Text>
          <Text style={styles.statLabel}>Jours planifies</Text>
        </View>
        <View style={styles.statBox}>
          <Text style={styles.statValue}>{periodAffectations.length}</Text>
          <Text style={styles.statLabel}>Affectations</Text>
        </View>
      </View>
    </View>
  );
};

// =============================================================================
// DOCUMENT PRINCIPAL
// =============================================================================

export const PlanningPDF = ({ viewMode, currentDate, affectations, entrepriseNom, activeFilters }: PlanningPDFProps) => {
  return (
    <Document>
      <Page size="A4" orientation="landscape" style={[sharedStyles.page, styles.pageOverrides]}>
        <Header viewMode={viewMode} currentDate={currentDate} entrepriseNom={entrepriseNom} activeFilters={activeFilters} />

        {viewMode === 'semaine' ? (
          <WeekView currentDate={currentDate} affectations={affectations} />
        ) : (
          <MonthView currentDate={currentDate} affectations={affectations} />
        )}

        <Stats affectations={affectations} currentDate={currentDate} viewMode={viewMode} />

        <Footer />
      </Page>
    </Document>
  );
};

export default PlanningPDF;
